import { useState } from "react";
import { Link } from "react-router-dom";

import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import { PublicApiError, requestMemberPasswordReset } from "@/features/public/api";
import { extractSenegalPhoneDigits, getSenegalPhoneError, toSenegalPhoneE164 } from "@/shared/phone/senegalPhone";

type ForgotPasswordField = "phone" | "email";

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function getFirstServerFieldError(error: PublicApiError, field: string) {
  const details = error.details as
    | {
        errors?: {
          fieldErrors?: Record<string, string[] | undefined>;
        };
      }
    | undefined;

  return details?.errors?.fieldErrors?.[field]?.find(Boolean);
}

function getInputClassName(hasError: boolean) {
  return `w-full rounded-xl border px-4 py-3 text-sm outline-none transition-all ${
    hasError
      ? "border-red-300 bg-red-50/60 focus:border-red-400 focus:ring-2 focus:ring-red-200"
      : "border-gray-200 bg-white focus:border-[#E8521A] focus:ring-2 focus:ring-orange-100"
  }`;
}

function SenegalPhoneInput({
  value,
  onChange,
  error
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-gray-800">Numéro de téléphone</label>
      <div className={`flex overflow-hidden rounded-xl border bg-white transition-all ${error ? "border-red-300 focus-within:ring-2 focus-within:ring-red-200" : "border-gray-200 focus-within:border-[#E8521A] focus-within:ring-2 focus-within:ring-orange-100"}`}>
        <div className="flex items-center border-r border-gray-100 bg-gray-50 px-4 text-sm font-bold text-gray-600">+221</div>
        <input
          type="tel"
          inputMode="numeric"
          required
          value={value}
          onChange={(event) => onChange(extractSenegalPhoneDigits(event.target.value))}
          className="w-full bg-transparent px-4 py-3 text-sm outline-none"
          placeholder="77xxxxxxx"
        />
      </div>
      {error ? <p className="mt-2 text-sm font-medium text-red-600">{error}</p> : null}
    </div>
  );
}

export default function ForgotPasswordPage() {
  const [form, setForm] = useState({
    phone: "",
    email: ""
  });
  const [errors, setErrors] = useState<Partial<Record<ForgotPasswordField, string>>>({});
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field: ForgotPasswordField, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) {
        return current;
      }

      return { ...current, [field]: undefined };
    });
  };

  const validateForm = () => {
    const nextErrors: Partial<Record<ForgotPasswordField, string>> = {};
    const phoneError = getSenegalPhoneError(form.phone);

    if (phoneError) {
      nextErrors.phone = phoneError;
    }

    if (!form.email.trim()) {
      nextErrors.email = "L'adresse email est obligatoire.";
    } else if (!isValidEmail(form.email.trim())) {
      nextErrors.email = "Merci de saisir une adresse email valide.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);
    const nextErrors = validateForm();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmitting(true);

    try {
      const normalizedEmail = form.email.trim().toLowerCase();

      await requestMemberPasswordReset({
        phone: toSenegalPhoneE164(form.phone),
        email: normalizedEmail
      });

      setForm({ phone: "", email: "" });
      setErrors({});
      setFeedback({
        type: "success",
        message: `Le nouveau mot de passe a été envoyé à l’adresse e-mail ${normalizedEmail}.`
      });
    } catch (caughtError) {
      if (caughtError instanceof PublicApiError) {
        const phoneError = getFirstServerFieldError(caughtError, "phone");
        const emailError = getFirstServerFieldError(caughtError, "email");

        if (phoneError || emailError) {
          setErrors({
            ...(phoneError ? { phone: phoneError } : {}),
            ...(emailError ? { email: emailError } : {})
          });
          return;
        }
      }

      setFeedback({
        type: "error",
        message: caughtError instanceof Error ? caughtError.message : "Impossible de réinitialiser le mot de passe."
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5]">
      <Navbar />

      <main className="mx-auto flex min-h-[calc(100vh-180px)] max-w-6xl items-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-orange-100 bg-white shadow-[0_28px_90px_rgba(232,82,26,0.10)] lg:grid-cols-[0.95fr_1.05fr]">
          <div className="relative hidden min-h-[520px] overflow-hidden bg-[#1A8C7A] lg:block">
            <img
              src="https://readdy.ai/api/search-image?query=senegal%20sports%20association%20members%20professional%20community%20orange%20green%20light%20modern%20office%20portrait&width=900&height=900&seq=forgot-password-asc&orientation=squarish"
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(145deg,rgba(26,140,122,0.88),rgba(232,82,26,0.48))]"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <p className="text-xs font-bold uppercase tracking-[0.26em] text-white/75">ASC Sonatel</p>
              <h1 className="mt-3 text-3xl font-bold leading-tight">Récupération du compte membre</h1>
              <p className="mt-3 max-w-sm text-sm leading-6 text-white/82">
                Le nouveau mot de passe est envoyé uniquement si le téléphone et l'email correspondent au même compte validé.
              </p>
            </div>
          </div>

          <section className="px-5 py-8 sm:px-8 lg:px-12 lg:py-12">
            <Link to="/formulaires?tab=login" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A8C7A] hover:text-[#146f61]">
              <i className="ri-arrow-left-line"></i>
              Retour à la connexion
            </Link>

            <div className="mt-8">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#E8521A]">Mot de passe oublié</p>
              <h2 className="mt-3 text-2xl font-bold text-[#1A1A1A] sm:text-3xl">Recevoir un nouveau mot de passe</h2>
              <p className="mt-3 text-sm leading-6 text-gray-600">
                Renseigne le numéro de téléphone et l'adresse email liés à ton compte membre.
              </p>
            </div>

            {feedback ? (
              <div className={`mt-6 rounded-2xl px-4 py-3 text-sm font-medium ${feedback.type === "success" ? "border border-emerald-100 bg-emerald-50 text-emerald-700" : "border border-red-100 bg-red-50 text-red-600"}`}>
                {feedback.message}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="mt-7 space-y-5">
              <SenegalPhoneInput
                value={form.phone}
                onChange={(value) => updateField("phone", value)}
                error={errors.phone}
              />

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-800">Adresse email</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  className={getInputClassName(Boolean(errors.email))}
                  placeholder="prenom.nom@example.com"
                  autoComplete="email"
                />
                {errors.email ? <p className="mt-2 text-sm font-medium text-red-600">{errors.email}</p> : null}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#E8521A] px-5 py-4 text-sm font-bold text-white transition hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={submitting ? "ri-loader-4-line animate-spin" : "ri-mail-send-line"}></i>
                {submitting ? "Envoi en cours..." : "Envoyer le nouveau mot de passe"}
              </button>
            </form>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
